import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import type { Placement } from '@floating-ui/react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { Children, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Interpolation } from 'styled-components';
import styled, { css } from 'styled-components';

import { ElementTestId } from 'enums/testing';
import { variants } from 'utils/styledUtils';

import {
  BORDER_RADIUS_050,
  BORDER_RADIUS_200,
  FONT_SIZE_13,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  LINE_HEIGHT_2,
  NEUTRAL_0,
  NEUTRAL_900,
  SPACE_100,
  SPACE_200,
  SPACE_300,
  Z_INDEX_5,
} from 'styles/tokens';
import { LINE_HEIGHT_CONDENSED } from 'styles/typography';

export type { Placement } from '@floating-ui/react';

const TOOLTIP_ANIMATION_DURATION_MS = 100;
/**
 * Delay before the show animation starts in milliseconds
 */
const TOOLTIP_SHOW_DELAY_MS = 300;

const TOOLTIP_SLIDE_DISTANCE = 10;

const TooltipContainer = styled.div`
  background: ${NEUTRAL_900};
  border-radius: ${BORDER_RADIUS_200};
  pointer-events: none;
`;

const TooltipPositioner = styled.div`
  pointer-events: none;
  position: absolute;
  z-index: ${Z_INDEX_5};
`;

export type TooltipStyle = 'condensed' | 'normal';

const normalTooltipContentsStyle = css`
  font-size: ${FONT_SIZE_13};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  line-height: ${LINE_HEIGHT_CONDENSED};
  padding: ${SPACE_200} ${SPACE_300};
`;
const condensedTooltipContentsStyle = css`
  font-size: ${FONT_SIZE_13};
  font-weight: ${FONT_WEIGHT_SEMIBOLD};
  line-height: ${LINE_HEIGHT_2};
  padding: ${SPACE_100} ${SPACE_200};
`;

const TooltipContents = styled.div<{ $tooltipContentCss?: Interpolation<object>; $tooltipStyle?: TooltipStyle }>`
  color: ${NEUTRAL_0};
  max-width: 280px;

  ${variants<TooltipStyle>(
    '$tooltipStyle',
    {
      condensed: condensedTooltipContentsStyle,
      normal: normalTooltipContentsStyle,
    },
    'normal'
  )};

  ${props => props.$tooltipContentCss}
`;

const ARROW_SIZE = 12;

const THIRD_ARROW_SIZE = ARROW_SIZE / 3;

const BASE_ARROW_TRANSFORM = 'rotate(45deg)';

const Arrow = styled.div`
  background: ${NEUTRAL_900};
  border-radius: ${BORDER_RADIUS_050};
  height: ${ARROW_SIZE}px;
  position: absolute;
  width: ${ARROW_SIZE}px;
`;

type PlacementSide = 'bottom' | 'left' | 'right' | 'top';

// Keep this numeric offset aligned with SPACE_100 (4px).
const DEFAULT_TOOLTIP_OFFSET = 4;

/**
 * Props returned by getReferenceProps for spreading onto the reference element
 */
export type ReferenceProps = Record<string, unknown>;

/**
 * Props for the internal TooltipContent component.
 * This component handles positioning, animation, and rendering of the tooltip.
 * Most consumers should use the Tooltip wrapper component instead.
 */
export interface TooltipContentProps {
  /**
   * The element that the tooltip is positioned against
   */
  anchor: RefObject<Element | null>;
  /**
   * The contents of the tooltip
   */
  children: ReactNode;
  /**
   * Whether the tooltip is currently open (controlled mode). Takes precedence over showOnHover.
   * When undefined, tooltip uses hover/focus mode based on showOnHover.
   */
  isOpen?: boolean;
  /**
   * Extra spacing in pixels between the anchor and the tooltip. Defaults to `4`.
   */
  margin?: number;
  /**
   * Callback that receives the reference props from Floating UI's useInteractions.
   * These props should be spread onto the reference/anchor element for proper event handling.
   * Required for hover/focus interactions to work reliably across all environments.
   *
   * @param getProps - Function that accepts user props and returns merged props.
   *                   Pass the child element's event handlers to properly merge them with tooltip handlers.
   */
  onReferenceProps?: (getProps: (userProps?: ReferenceProps) => ReferenceProps) => void;
  /**
   * Where the tooltip should appear relative to the anchor. Defaults to 'left-start'.
   *
   * ```
   *              top-start         top           top-end
   *                 |               |               |
   *                 v               v               v
   *            +----^----+     +----^----+     +----^----+
   *            |    .    |     |    .    |     |    .    |
   *            +---------+     +---------+     +---------+
   *
   *  +---------+                                        +---------+
   *  |.        | <-- left-start         right-start --> |        .|
   *  +---------+                                        +---------+
   *
   *  +---------+                                        +---------+
   *  |.        | <-- left       [ANCHOR]      right --> |        .|
   *  +---------+                                        +---------+
   *
   *  +---------+                                        +---------+
   *  |.        | <-- left-end             right-end --> |        .|
   *  +---------+                                        +---------+
   *
   *            +---------+     +---------+     +---------+
   *            |    .    |     |    .    |     |    .    |
   *            +----v----+     +----v----+     +----v----+
   *                 ^               ^               ^
   *                 |               |               |
   *           bottom-start      bottom        bottom-end
   *
   * Legend:
   * - [ANCHOR] = The element the tooltip is attached to
   * - . = Arrow position on the tooltip
   * - start = Arrow aligned to the start (top/left) of the tooltip edge
   * - end = Arrow aligned to the end (bottom/right) of the tooltip edge
   * - No suffix = Arrow centered on the tooltip edge
   * ```
   */
  placement?: Placement;
  /**
   * Whether to show tooltip on hover/focus. Enables automatic hover and focus interactions.
   * Defaults to `true`.
   */
  showOnHover?: boolean;
  /**
   * The style applied to the tooltip. Defaults to `'normal'`.
   */
  styleVariant?: TooltipStyle;
  /**
   * Optional CSS to be applied to the tooltip content
   */
  toolTipContentCss?: Interpolation<object>;
}

const getInitialTransform = (side: PlacementSide): string => {
  switch (side) {
    case 'bottom': {
      return `translateY(${TOOLTIP_SLIDE_DISTANCE}px)`;
    }

    case 'left': {
      return `translateX(${TOOLTIP_SLIDE_DISTANCE}px)`;
    }

    case 'right': {
      return `translateX(-${TOOLTIP_SLIDE_DISTANCE}px)`;
    }

    case 'top': {
      return `translateY(-${TOOLTIP_SLIDE_DISTANCE}px)`;
    }
  }
};

const getPlacementSide = (placement: Placement): PlacementSide => placement.split('-', 1)[0] as PlacementSide;

const DEFAULT_PLACEMENT: Placement = 'left-start';

/**
 * Internal component that handles tooltip positioning, animation, and rendering.
 * Uses Floating UI for positioning and manages animation states.
 *
 * Most consumers should use the Tooltip wrapper component instead of this directly.
 * This component is exported for advanced use cases that need direct control over refs and positioning.
 */
export const TooltipContent = ({
  anchor,
  children,
  isOpen: isOpenControlled,
  margin = DEFAULT_TOOLTIP_OFFSET,
  onReferenceProps,
  placement = DEFAULT_PLACEMENT,
  showOnHover = true,
  styleVariant = 'normal',
  toolTipContentCss,
}: TooltipContentProps) => {
  const arrowRef = useRef<HTMLDivElement>(null);

  const [isOpenInternal, setIsOpenInternal] = useState(false);

  const placementSide = getPlacementSide(placement);
  const hasChildren = useMemo(() => Children.count(children) > 0, [children]);

  const showArrow = styleVariant === 'normal';

  const open = isOpenControlled === undefined ? (showOnHover ? isOpenInternal : false) : isOpenControlled;

  const {
    context,
    floatingStyles,
    middlewareData,
    placement: finalPlacement,
    refs,
  } = useFloating({
    middleware: [
      offset(margin),
      flip(),
      shift({ padding: 5 }),
      /*
       * The ref object is handed to the middleware, not dereferenced here: Floating UI's React
       * wrapper reads `.current` lazily at compute time, so the arrow is positioned on the very
       * first pass. Passing a state value instead leaves the arrow unpositioned for one frame.
       */
      // oxlint-disable-next-line react/refs
      ...(showArrow ? [arrow({ element: arrowRef })] : []),
    ],
    onOpenChange: setIsOpenInternal,
    open,
    placement,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: TOOLTIP_SHOW_DELAY_MS },
    enabled: showOnHover,
  });

  const focus = useFocus(context, {
    enabled: showOnHover,
  });

  const role = useRole(context, { role: 'tooltip' });

  const { getFloatingProps, getReferenceProps } = useInteractions([hover, focus, role]);

  /**
   * Call onReferenceProps callback with getReferenceProps function from useInteractions.
   * Using useLayoutEffect ensures this runs synchronously before browser paint,
   * so event handlers are attached before any user interactions (critical for Chromatic tests).
   */
  useLayoutEffect(() => {
    if (onReferenceProps) {
      onReferenceProps(getReferenceProps);
    }
  }, [getReferenceProps, onReferenceProps]);

  /**
   * Floating UI transition styles hook.
   * Handles all animation state and provides CSS properties for smooth transitions.
   * Animation is always enabled - use Chromatic's pauseAnimationAtEnd for visual testing.
   */
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: {
      close: TOOLTIP_ANIMATION_DURATION_MS,
      open: TOOLTIP_ANIMATION_DURATION_MS,
    },
    initial: {
      opacity: 0,
      transform: getInitialTransform(placementSide),
    },
    open: {
      opacity: 1,
      transform: 'translate(0)',
    },
  });

  /**
   * Set the reference element from the anchor prop.
   * This syncs the external anchor ref with Floating UI's reference system.
   */
  useEffect(() => {
    refs.setReference(anchor.current);
  }, [anchor, refs]);

  const arrowStyle = useMemo<CSSProperties>(() => {
    const arrowData = middlewareData.arrow;
    if (!arrowData) {
      return {};
    }

    const { x, y } = arrowData;

    const staticSide = {
      bottom: 'top',
      left: 'right',
      right: 'left',
      top: 'bottom',
    }[getPlacementSide(finalPlacement)];

    const style: CSSProperties = {
      [staticSide]: `-${THIRD_ARROW_SIZE}px`,
      transform: BASE_ARROW_TRANSFORM,
    };

    if (x !== undefined) {
      style.left = `${x}px`;
    }
    if (y !== undefined) {
      style.top = `${y}px`;
    }

    return style;
  }, [middlewareData.arrow, finalPlacement]);

  if (!hasChildren || !isMounted) {
    return null;
  }

  return (
    <FloatingPortal id="tooltip-root">
      <TooltipPositioner ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
        <TooltipContainer data-testid={ElementTestId.TOOLTIP_SURFACE} style={transitionStyles}>
          {showArrow ? <Arrow ref={arrowRef} style={arrowStyle} /> : null}
          <TooltipContents
            $tooltipContentCss={toolTipContentCss}
            $tooltipStyle={styleVariant}
            data-testid={ElementTestId.TOOLTIP_CONTENT}
          >
            {children}
          </TooltipContents>
        </TooltipContainer>
      </TooltipPositioner>
    </FloatingPortal>
  );
};
